import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import authService from '@/services/auth-service';
import { useI18n } from '@/hooks/use-i18n';
import { ErrorHandler } from '@/lib/error-handler';
import type { UserEntity } from '@trackv16/types';

export const profileKeys = {
  all: ['profile'] as const,
  current: () => [...profileKeys.all, 'current'] as const,
};

export function useProfile() {
  return useQuery({
    queryKey: profileKeys.current(),
    queryFn: () => authService.getCurrentUser(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: Partial<UserEntity>) => authService.updateProfile(data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(profileKeys.current(), updatedUser);
      queryClient.invalidateQueries({ queryKey: profileKeys.current() });
      toast.success(
        t('profile.update.success', 'Profile updated successfully')
      );
    },
    onError: (error: unknown) => {
      ErrorHandler.handle(error, 'profile');
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      queryClient.clear();
      toast.success(t('auth.logoutStatus.success', 'Logged out successfully'));
      // Redirect to login
      window.location.href = '/auth/login';
    },
    onError: (error: unknown) => {
      ErrorHandler.handle(error, 'auth');
    },
  });
}
